
import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase/client';
import { Ticket } from '@phosphor-icons/react';
import { DashboardWidget, WidgetKopf, WidgetLaedt } from './DashboardWidget';
import { istMeineZustaendigkeit, type ScopeNutzer } from '../../lib/dashboard/scope';

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  'neu': { label: 'Neu', color: 'var(--dw-st-neu)' },
  'offen': { label: 'Offen', color: 'var(--dw-st-offen)' },
  'in_bearbeitung': { label: 'In Bearbeitung', color: 'var(--dw-st-bearbeitung)' },
  'warten_auf_rueckmeldung': { label: 'Rückmeldung', color: 'var(--dw-st-rueckmeldung)' },
  'warten_auf_dienstleister': { label: 'Dienstleister', color: 'var(--dw-st-dienstleister)' },
  'eskaliert': { label: 'Eskaliert', color: 'var(--dw-st-eskaliert)' },
  'erledigt': { label: 'Erledigt', color: 'var(--dw-st-erledigt)' },
  'geschlossen': { label: 'Geschlossen', color: 'var(--dw-st-geschlossen)' },
};

interface TicketStatusChartProps {
  onNavigate?: (path: string) => void;
  /** Mitarbeitende: nur Tickets ihrer eigenen bzw. Team-Zuständigkeit zählen. */
  nurEigene?: boolean;
  nutzer?: ScopeNutzer;
}

const TicketStatusChart: React.FC<TicketStatusChartProps> = ({ onNavigate, nurEigene, nutzer }) => {
  const [data, setData] = useState<{ status: string; count: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nurEigene, nutzer?.id]);

  const fetchData = async () => {
    try {
      // Archivierte (Papierkorb-)Tickets ausblenden — TicketListe.tsx zeigt sie in keiner normalen Ansicht.
      const { data: alle } = await supabase
        .from('tickets')
        .select('status, zustaendig_id, zustaendig_typ')
        .is('geloescht_am', null);

      if (!alle) return;
      const tickets = nurEigene && nutzer
        ? alle.filter((t) => istMeineZustaendigkeit(t.zustaendig_id, t.zustaendig_typ, nutzer))
        : alle;

      const counts: Record<string, number> = {};
      tickets.forEach(t => {
        counts[t.status] = (counts[t.status] || 0) + 1;
      });

      const result = Object.entries(counts)
        .map(([status, count]) => ({ status, count }))
        .sort((a, b) => b.count - a.count);

      setData(result);
    } catch (err) {
      console.error('Error fetching ticket stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const total = data.reduce((sum, d) => sum + d.count, 0);
  const offene = data.filter(d => !['erledigt', 'geschlossen'].includes(d.status)).reduce((sum, d) => sum + d.count, 0);

  // SVG Donut
  const radius = 56;
  const circumference = 2 * Math.PI * radius;
  let currentOffset = 0;

  const segments = data.map((d, i) => {
    const pct = total > 0 ? d.count / total : 0;
    const dashLength = pct * circumference;
    const offset = currentOffset;
    currentOffset += dashLength;
    return { ...d, pct, dashLength, offset, color: STATUS_CONFIG[d.status]?.color || 'var(--dw-st-sonst)' };
  });

  // Überfahrenes Segment (Ring oder Legende) zeigt seine Zahl in der Mitte, sonst die offenen Tickets
  const mitte = hoveredIndex !== null ? segments[hoveredIndex] : null;

  return (
    <DashboardWidget farbe="--dw-gruen" icon={Ticket} ariaLabel="Tickets"
      onClick={() => onNavigate?.('/admin/tickets')} tip="Tickets – Zur Ticketliste">
      <WidgetKopf farbe="--dw-gruen" icon={Ticket} titel="Tickets" untertitel="Status-Übersicht" />

      {loading ? (
        <WidgetLaedt />
      ) : total > 0 ? (
        <div className="flex flex-wrap items-center" style={{ gap: 16 }}>
          {/* Donut Chart */}
          <div className="relative shrink-0" style={{ width: 108, height: 108 }}>
            <svg viewBox="0 0 140 140" width={108} height={108}>
              <circle cx="70" cy="70" r={radius} fill="none" stroke="var(--fill2)" strokeWidth="16" />
              {segments.map((seg, i) => (
                <circle
                  key={seg.status}
                  cx="70"
                  cy="70"
                  r={radius}
                  fill="none"
                  stroke={seg.color}
                  strokeWidth={hoveredIndex === i ? 20 : 16}
                  strokeDasharray={`${seg.dashLength} ${circumference - seg.dashLength}`}
                  strokeDashoffset={-seg.offset}
                  strokeLinecap="butt"
                  transform="rotate(-90 70 70)"
                  onMouseEnter={() => setHoveredIndex(i)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  style={{ transition: 'stroke-width .2s, opacity .2s', opacity: hoveredIndex !== null && hoveredIndex !== i ? 0.4 : 1 }}
                />
              ))}
            </svg>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <span className="tabular-nums" style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.03em', color: 'var(--ink)' }}>{mitte ? mitte.count : offene}</span>
              <span className="max-w-[80px] truncate text-center" style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--ink3)' }}>
                {mitte ? (STATUS_CONFIG[mitte.status]?.label || mitte.status) : 'Offen'}
              </span>
            </div>
          </div>

          {/* Legend */}
          <div className="flex flex-1 flex-col" style={{ minWidth: 130, gap: 5 }}>
            {segments.slice(0, 5).map((seg, i) => (
              <div
                key={seg.status}
                className="flex items-center"
                onMouseEnter={() => setHoveredIndex(i)}
                onMouseLeave={() => setHoveredIndex(null)}
                style={{ gap: 8, fontSize: 12.5, transition: 'opacity .2s', opacity: hoveredIndex !== null && hoveredIndex !== i ? 0.45 : 1 }}
              >
                <span className="shrink-0" style={{ width: 9, height: 9, borderRadius: 3, background: seg.color }} />
                <span className="min-w-0 flex-1 truncate" style={{ fontWeight: 600, color: 'var(--ink2)' }}>
                  {STATUS_CONFIG[seg.status]?.label || seg.status}
                </span>
                <span className="shrink-0 tabular-nums" style={{ fontWeight: 800, color: 'var(--ink)' }}>{seg.count}</span>
              </div>
            ))}
            {segments.length > 5 && (
              <span style={{ paddingLeft: 17, fontSize: 12, fontWeight: 600, color: 'var(--ink3)' }}>+{segments.length - 5} weitere</span>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center" style={{ padding: '22px 0', gap: 8, color: 'var(--ink3)' }}>
          <Ticket size={30} />
          <span style={{ fontSize: 13.5, fontWeight: 700 }}>Keine Tickets vorhanden</span>
        </div>
      )}
    </DashboardWidget>
  );
};

export default TicketStatusChart;
